import React, { useEffect, useRef, useState } from 'react';
import 'styles/index.css';

import lz from 'lzutf8';
import moment from 'moment-timezone';
import update from 'immutability-helper';
import { useDeviceSelectors } from 'react-device-detect';

import API from 'files/api.js';
import AddressLookupField from 'views/AddressLookupField.js';
import Alert from 'views/Alert.js';
import AlertStack from 'views/AlertStack.js';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import ContentManager from 'managers/Content.js';
import ContentViewer, { AnimatedContainer, workflowLandingComponents } from 'views/ContentViewer.js';
import DatePickerField from 'views/DatePickerField.js';
import { EndIndex } from 'structure/Layer.js';
import FilePickerField from 'views/FilePickerField.js';
import LottieView from 'views/Lottie.js';
import QueryString from 'query-string';
import Request from 'files/Request.js';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';

const Content = ContentManager.new();
const shouldRedirect = true;

const App = () => {

    const contentContainerRef = useRef(null);
    const dealershipRef = useRef(null);
    const history = useRef({});
    const userRef = useRef(null);

    const [alerts, setAlerts] = useState([]);
    const [article, setArticle] = useState(null);
    const [browserRequest, setBrowserRequest] = useState(null);
    const [content, setContent] = useState(null);
    const [contentInvalid, setContentInvalid] = useState(false);
    const [identifiers, data] = useDeviceSelectors(window.navigator.userAgent);
    const [landingProps, setLandingProps] = useState({});
    const [layers, setLayers] = useState([]);
    const [layerIndex, setLayerIndex] = useState([]);
    const [loading, setLoading] = useState(false);
    const [request, setRequest] = useState(null);
    const [responseAttachment, setResponseAttachment] = useState(null);
    const [responseText, setResponseText] = useState(null);
    const [responseValue, setResponseValue] = useState({});
    const [response, setResponse] = useState(null);
    const [seconds, setSeconds] = useState(10);
    const [responseSubmitted, setResponseSubmitted] = useState(false);
    const [session, setSession] = useState(null);
    const [size, setSize] = useState({ height: window.innerHeight, width: window.innerWidth });
    const [theme, setTheme] = useState('light');

    const onActionButtonClick = (id, options) => {
        onSubmitLandingData({
            sender: {
                id: id,
                type: Utils.senderTypes.button,
                ...options
            }
        });
    }

    const onArticleFeedbackClick = helpful => {

        // submit feedback if user indicated that article was helpful
        if(helpful) {
            onSubmitArticleFeedback({ value: helpful });
            return;
        }

        // request customer feedback if user indicated that article was not helpful
        let email = null;
        let response = null;
        utils.alert.show({
            title: 'Sorry About That',
            message: `We're sorry to hear that this article has not helpful for you. What can we do to improve this content?`,
            component: (
                <div style={{
                    padding: 12,
                    width: '100%'
                }}>
                    <TextField
                    containerStyle={{ marginBottom: 8 }}
                    onChange={text => email = text} 
                    placeholder={'Email Address (optional)'} />

                    <TextField
                    expandWithText={true}
                    onChange={text => response = text} 
                    placeholder={'Please type your response here...'} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Done',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSubmitArticleFeedback({ 
                        email_address: email,
                        message: response,
                        value: helpful 
                    });
                    return;
                }
            }
        });
    }

    const onCloseAlert = id => {
        setAlerts(alerts => {
            return alerts.filter(alert => id !== alert.id);
        });
    }

    const onCloseLayer = id => {

        // update layer visibility and layer index collection
        let next = layers.map(layer => {
            if(layer.id === id) {
                layer.visible = false;
            }
            return layer;
        });

        setLayerIndex(ids => {
            return ids.filter(prev => {
                return prev !== id;
            });
        });

        // filter next layers down to only visible layers
        let current = next.filter(layer => {
            return layer.visible !== false;
        });

        // notify listeners that layer stack order has changed
        if(current.length > 0) {
            utils.events.emit('layer_focus_change', current[current.length - 1].id);
        }

        // update layers with remaining visible layers if applicable
        if(current.length > 0) {
            setLayers(next);
            return;
        }

        // reset layers array if no visible layers are found
        setLayers([]);
        console.log('layers reset');
    }

    const onContentChange = content => {

        // prevnt moving forward if no content is available
        if(!content) {
            return;
        }

        // preform a content redirect if a url was provided
        if(content.url) {

            // perform a redirection immediately is a delay was not requested
            if(session.options && session.options.redirection_delay === false) {
                onPerformRedirection(content);
                return;
            }

            // perform redirect after a delay
            let interval = setInterval(() => {
                setSeconds(seconds => {
                    if(seconds === 0) {
                        clearInterval(interval);
                    }
                    return seconds > 0 ? seconds - 1 : 0;
                });
            }, 1000);
        }
    }

    const onContentContainerSizeChange = () => {

        // notify parent windows of content change
        setTimeout(() => {
            let height = contentContainerRef.current && contentContainerRef.current.clientHeight;
            window.postMessage(`height:${height}`, '*');
        }, 500);
    }

    const onHistoryStateChange = evt => {
        try {
      
            if(!evt || !evt.state || !evt.state.detail) {
                return;
            }
            let { requestor } = evt.state.detail;
            let content = history.current[requestor];
            if(!content) {
                throw new Error('This content is no longer available');
            }
            onPrepareContent(content);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue navigating through your content. ${e.message || 'An unknown error occurred'}`,
                onClick: () => window.location.reload()
            });
        }
    }

    const onOpenLayer = async props => {

        // check if layer is already open
        let layer = layers.find(prev => prev.visible !== false && prev.id === props.id);
        if(layer) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'There is already a window open for this content.'
            });
            return;
        }

        // add new layer props to layer stack
        setLayers(layers => update(layers, { $push: [props] }));

        // timeout is needed otherwise layers may intermitently render in a reverse order?
        await Utils.sleep(0.1);
        setLayerIndex(ids => update(ids, { $unshift: [props.id] }));

        // notify listeners that layer stack order has changed
        setTimeout(() => utils.events.emit('layer_focus_change', props.id), 250);
    }

    const onPerformRedirection = target => {

        // determine if the action sender requested a new window for the url content
        let { sender, url } = target || content;
        if(sender && sender.open_in_new_window === true) {
            window.open(url);
            return;
        }

        // fallback to opening content in current window context
        window.location.href = url;
    }

    const onPrepareContent = content => {
        try {

            // prevent moving forward if no content was returned
            if(!content) {
                utils.alert.show({
                    title: 'Oops!',
                    message: `There was an issue preparing your content. This support workflow may not be setup correctly.`
                });
                return;
            }

            // decode landing layout props and set content if applicable
            if(content.landing) {
                let data = shouldDecodeLandingContent(content.landing.content.layout) ? lz.decompress(lz.decodeBase64(content.landing.content.layout)) : content.landing.content.layout;
                content.landing.content.layout = data;
            }

            // check if url is present and prevent moving forward if no url is found
            if(!content.landing && !content.url) {
                setContentInvalid(true);
                return;
            }

            // forward content url to opener if "open_in_new_window" is requested
            if(content.sender && content.sender.open_in_new_window === true) {
                onContentChange(content);
                return;
            }

            // update history if a requestor is found
            if(content.requestor) {
                onUpdateHistory(content);
            }

            // set content as-is if no standard landing is present
            setContent(content);

        } catch(e) {
            console.error(e.message);
        }
    }

    const onRequestLocation = async () => {
        return new Promise(async (resolve, reject) => {
            try {
                // check that browser is able to access the geolocation api
                if(!navigator.geolocation) {
                    throw new Error('Your browser is not able to access the geolocation api');
                }

                // check if a permission alert should be presented
                // request permission if this is the first time or if the last time was on a different day
                let timestamp = localStorage.getItem('request_location_permission');
                if(timestamp && moment.unix(timestamp).isSame(moment(), 'day')) {
                    let location = await onRequestLocationCoordinate();
                    resolve(location);
                    return;
                }

                // request user permission to access their fine location
                utils.alert.show({
                    title: 'Location Services',
                    message: `We would like to use your location to provide content relavent to your area.`,
                    buttons: [{
                        key: 'confirm',
                        title: 'Sounds Good',
                        style: 'default'
                    },{
                        key: 'decline',
                        title: `Don't Use My Location`,
                        style: 'destructive'
                    }],
                    onClick: async key => {
                        try {
                            // update local variable representing the need to show permission alert
                            localStorage.setItem('request_location_permission', moment().unix());

                            // prevent moving forward if location access was not granted
                            if(key !== 'confirm') {
                                resolve();
                                return;
                            }
                            let location = await onRequestLocationCoordinate();
                            resolve(location);
                        }  catch(e) {
                            reject(e);
                        }
                    }
                });

            } catch(e) {
                reject(e);
            }
        })
    }

    const onRequestLocationCoordinate = async () => {
        return new Promise(resolve => {

            // use simulated location is application is being ran in a development environment
            // https is required before geolocation api will return a result
            if(API.dev_env === true) {
                console.warn('setting development location coordinate...');
                resolve({ lat: 33.007966, long: -97.037193 });
                return;
            }

            // request browser permission to access geolocation api
            navigator.geolocation.getCurrentPosition(({ coords }) => {
                console.warn('setting location coordinate...', coords);
                resolve({ lat: coords.latitude, long: coords.longitude });
            }, () => {
                resolve(null);
            });
        })
    }

    const onRequestPermissions = async () => {
        try {
            let results = {};
            let { request_location } = session.options || {};
            if(request_location === true) {
                results.location = await onRequestLocation();
            }
            onSubmitDeviceIdentifiers(results);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue requesting app permissions. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSetStyleSheetProperties = () => {
        setTheme(window.theme);
        document.body.className = window.theme;
        document.documentElement.style.setProperty('--theme', window.theme);
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textfield());
        document.documentElement.style.setProperty('--border', Appearance.colors.border());
        document.documentElement.style.setProperty('--border', Appearance.colors.border());
        document.querySelector('meta[name="theme-color"]').setAttribute('content',  Utils.isMobile() ? Appearance.colors.panel.background() : Appearance.colors.background());
    }

    const onSubmitArticleFeedback = async props => {
        try {
            setLoading(true);
            await Request.post(utils, '/support/', {
                ...props,
                id: article.id,
                type: 'new_kb_article_feedback'
            });

            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: 'Thank you for sharing your feedback about this knowledge base article'
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue submitting your feedback. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSubmitLandingData = async props => {
        try {

            // determine if field validation needs to take place
            if(content.landing && content.landing.content && props.sender.ignore_field_requirements !== true) {

                // look for at least one required field that has not been completed
                let required = content.landing.content.fields && content.landing.content.fields.find(field => {
                    let value = utils.editor.field.get(field.identifier);
                    return field.required === true && (value === null || value === undefined);
                });

                // prevent moving forward if a required field was found
                if(required) {
                    utils.alert.show({
                        title: 'Just a Second',
                        message: `Please fill out ${require.title ? `the "${require.title}" field` : 'all required fields'} before moving on. Required fields have a red dot on the left hand side of the field.`
                    });
                    return;
                }
            }

            // submit landing data to server
            setLoading(props.sender.id);
            let response = await Request.post(utils, '/support/', {
                type: 'resume_session',
                session: {
                    interaction_id: content.interaction_id,
                    payload: { 
                        [content.requestor]: getFormattedLandingData()
                    },
                    requestor: content.requestor,
                    ...props
                }
            });

            // end loading and unmount current components if applicable
            setLoading(false);
            if(props.sender.open_in_new_window !== true) {
                utils.events.emit('unmount');
                setTimeout(() => {
                    setContent(content => ({
                        ...content,
                        landing: {
                            content: {
                                layout: '{"ROOT":{"type":"div","isCanvas":true,"props":{"style":{}},"displayName":"div","custom":[],"hidden":false,"linkedNodes":[]}}'
                            }
                        }
                    }));
                }, 500);
            }
            
            // set new content after components have unmounted
            setTimeout(onPrepareContent.bind(this, response.content), 500);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue submitting your request. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSubmitDeviceIdentifiers = async props => {
        try {
            // fetch workflow identifier from request
            if(!browserRequest || !browserRequest.id) {
                throw new Error('missing workflow private identifier');
            }

            // fetch device props from browser
            let identifiers = getDeviceIdentifierProps();
            if(!identifiers) {
                throw new Error('missing device identifiers');
            }

            // send device identifiers to server for interaction logging
            let { content } = await Request.post(utils, '/support/', {
                dealership_id: dealershipRef.current && dealershipRef.current.id,
                id: browserRequest.id,
                type: 'new_interaction',
                ...identifiers,
                ...props
            });
            onPrepareContent(content);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing your content. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSubmitRequestMessage = async () => {
        try {

            // validate required fields if applicable
            if(responseHasCustomFields() === true) {
                let required = response.content.value.find(item => item.required && !responseValue[item.id]);
                if(required) {
                    utils.alert.show({
                        title: 'Just a Second',
                        message: `Please fill out the "${required.title.toLowerCase()}" before moving on`
                    });
                    return;
                }
            }

            // prevent moving forward if a message was not provided
            if(responseHasCustomFields() === false && !responseText) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: 'Please provide a message before moving on'
                });
                return;
            }

            // submit request to server
            setLoading('submit');
            await Request.post(utils, '/support/', {
                attachment: responseAttachment,
                id: request.id,
                message: responseText,
                origin_response_id: response.id,
                type: 'new_external_request_response',
                values: responseValue
            });

            // end loading and show confirmation alert
            setLoading(false);
            setResponseSubmitted(true);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue submitting your message. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateHistory = content => {

        // prepare query string for url
        let { requestor = 'root' } = content || {};
        let queryString = `/support/`;

        // prepare query string variables
        let variables = {
            id: browserRequest.id,
            requestor: requestor
        };

        // add dealership variables to request if applicable
        if(dealershipRef.current) {
            variables.dealership_access_token = dealershipRef.current.access_token;
            variables.dealership_id = dealershipRef.current.id;
        }

        // add variables to query string
        variables = Object.keys(variables).map(key => `${key}=${variables[key]}`);
        queryString = `${queryString}?=${variables.join('&')}`;

        // update browser history with new query string
        window.history.pushState({ detail: content }, 'Get Support', queryString);
        history.current = update(history.current, {
            [requestor]: {
                $set: content
            }
        });
    }

    const onUpdateTheme = evt => {
        window.theme = evt.matches ? 'dark' : 'light';
        onSetStyleSheetProperties();
    }

    const onWindowSizeChange = () => {
        setSize({
            height: window.innerHeight,
            width: window.innerWidth
        });
    }

    const getBackgroundColor = () => {
        
        // determine if a session workflow background color is available
        if(session && session.options.display.background_color) {
            return session.options.display.background_color;
        }
        
        return Appearance.colors.background();
    }

    const getContent = () => {

        // present loading component while request state is being set
        if(!browserRequest || loading === true) {
            return getLoadingComponent()
        }

        // show a confirmation message if a response was successfully submitted
        if(responseSubmitted) {
            return (
                <AnimatedContainer
                utils={utils}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <img
                    src={'images/response-submitted.png'}
                    style={{
                        height: 100,
                        marginBottom: 18,
                        objectFit: 'contain',
                        overflow: 'hidden',
                        width: 100
                    }}/>

                    <span style={{
                        ...Appearance.textStyles.heavyTitle(),
                        marginBottom: 4,
                        textAlign: 'center'
                    }}>{'Response Submitted'}</span>

                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        marginBottom: 24,
                        textAlign: 'center',
                        whiteSpace: 'normal'
                    }}>{'We have received your message and will respond within the next 24-48 hours.'}</span>
                </AnimatedContainer>
            )
        }

        // show indicator that content was invalid or incomplete if applicable
        if(contentInvalid) {
            return (
                <AnimatedContainer
                utils={utils}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <img
                    src={'images/invalid-content.png'}
                    style={{
                        height: 100,
                        marginBottom: 18,
                        objectFit: 'contain',
                        overflow: 'hidden',
                        width: 100
                    }}/>

                    <span style={{
                        ...Appearance.textStyles.heavyTitle(),
                        marginBottom: 4,
                        textAlign: 'center'
                    }}>{'Invalid Content'}</span>

                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        marginBottom: 24,
                        textAlign: 'center',
                        whiteSpace: 'normal'
                    }}>{'We were unable to find a destination url for this workflow. This could mean that the support workflow or ending session rule have not been configured correctly.'}</span>
                </AnimatedContainer>
            )
        }

        // determine if a support request has been set
        if(request) {
            let firstName = request.targets.user ? request.targets.user.first_name : request.values.first_name;
            return (
                <AnimatedContainer
                utils={utils}
                style={{
                    ...Appearance.styles.unstyledPanel(),
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: Utils.isMobile() ? size.width - 24 : 500,
                    padding: '16px 20px 16px 20px',
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <span style={{
                        color: Appearance.colors.text(),
                        fontSize: 16,
                        fontWeight: 700,
                        marginBottom: 4,
                        width: '100%'
                    }}>
                        {`${firstName ? `Hello ${firstName}` : 'Hello'}, how can we help?`}
                    </span>
                    <span style={{
                        color: Appearance.colors.subText(),
                        fontSize: 12,
                        fontWeight: 600,
                        marginBottom: 24,
                        width: '100%'
                    }}>
                        {'Please use the text area below to share any questions or concerns that you have for us. Feel free to attach a photo or file with your message if you have anything else you would like to share with us. We typically respond to inquiries within 24-48 hours.'}
                    </span>

                    {getResponseInputComponents()}

                    <Button 
                    color={'primary'}
                    loading={loading === 'submit'}
                    onClick={onSubmitRequestMessage} 
                    text={'Submit'}
                    type={'large'} />
                </AnimatedContainer>
            )
        }

        // determine if a kb article has been set
        if(article && article.content) {
            return (
                <div style={{
                    ...Utils.isMobile() === false && Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    overflow: 'visible',
                    width: '100%'
                }}>
                    <ContentViewer
                    content={article.content.layout}
                    utils={utils} />
                    <div style={{
                        marginTop: 24,
                        padding: 16,
                        textAlign: 'center',
                        width: '100%'
                    }}>
                        <span style={{
                            color: Appearance.colors.subText(),
                            display: 'block',
                            fontSize: 12,
                            fontWeight: 500,
                            marginBottom: 8
                        }}>{'Was this article helpful?'}</span>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            width: '100%'
                        }}>
                            <img
                            className={'text-button'}
                            onClick={onArticleFeedbackClick.bind(this, true)}
                            src={'images/checkmark-green.png'}
                            style={{
                                height: 30,
                                marginRight: 4,
                                width: 30
                            }} />
                            <img
                            className={'text-button'}
                            onClick={onArticleFeedbackClick.bind(this, false)}
                            src={'images/red-x-icon.png'}
                            style={{
                                height: 30,
                                marginLeft: 4,
                                width: 30
                            }} />
                        </div>
                    </div>
                </div>
            )
        }

        // prevent error if no private identifier is provided
        if(!browserRequest.id) {
            return (
                <AnimatedContainer
                utils={utils}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <img
                    src={'images/logo.png'}
                    style={{
                        height: 150,
                        marginBottom: 18,
                        objectFit: 'contain',
                        overflow: 'hidden',
                        width: 150
                    }}/>

                    <span style={{
                        ...Appearance.textStyles.heavyTitle(),
                        marginBottom: 4,
                        textAlign: 'center'
                    }}>{'Get Support'}</span>

                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        marginBottom: 24,
                        textAlign: 'center',
                        whiteSpace: 'normal'
                    }}>{`This endpoint is not directly available. \nPlease contact support@ghscompanies.com if you were directed to this page.`}</span>

                    <Button
                    color={'primary'}
                    onClick={() => window.location.href = API.website} 
                    style={{ width: 140 }}
                    text={'Take Me Home'}/>
                </AnimatedContainer>
            )
        }

        // show landing content if a landing is returned from the interaction
        if(content && content.landing) {
            return (
                <div 
                id={'support_workflow_container'}
                ref={contentContainerRef}
                style={{
                    ...Utils.isMobile() === false && Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    overflow: 'visible',
                    width: '100%',
                    ...getWorkflowDisplayOptions(session.options.display)
                }}>
                    <ContentViewer
                    content={content.landing ? content.landing.content.layout : null}
                    session={session}
                    utils={utils} />
                </div>
            )
        }

        // show loading component if content has not yet been set or no redirection delay is requested
        if(!content || (session.options && session.options.redirection_delay === false)) {
            return getLoadingComponent()
        }

        // fallback to default redirection interface before redirecting to destination content
        return (
            <AnimatedContainer
            utils={utils}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: 12,
                width: '100%',
                textAlign: 'center'
            }}>
                <img
                src={'images/logo.png'}
                style={{
                    borderRadius: 75,
                    height: 125,
                    objectFit: 'cover',
                    overflow: 'hidden',
                    marginBottom: 18,
                    width: 125
                }}/>

                <span style={{
                    ...Appearance.textStyles.heavyTitle(),
                    marginBottom: 4,
                    textAlign: 'center'
                }}>{'Just a Moment'}</span>

                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    marginBottom: 4,
                    textAlign: 'center'
                }}>{`Please wait while we redirect you...`}</span>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    marginBottom: 4,
                    textAlign: 'center'
                }}>{`Click the button below if you are not redirected automatically.`}</span>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    marginBottom: 24,
                    textAlign: 'center'
                }}>{`Redirecting in ${seconds} ${seconds === 1 ? 'second' : 'seconds'}...`}</span>

                <Button
                text={'Take Me There'}
                color={'primary'}
                onClick={onPerformRedirection} />
            </AnimatedContainer>
        )
    }

    const getDeviceIdentifierProps = () => {
        try {

            // parse and decode optional mobile values
            let device = browserRequest.mobile ? atob(browserRequest.mobile) : null;
            device = device && JSON.parse(device);
            
            return identifiers && {
                agent: identifiers.getUA,
                browser: {
                    name: identifiers.browserName,
                    version: identifiers.browserVersion,
                    version_ext: identifiers.fullBrowserVersion
                },
                device: {
                    ...device,
                    is_native: device ? true : false
                },
                platform: {
                    name: device && device.platform || identifiers.osName,
                    version: device && device.version || identifiers.osVersion
                }
            }
        } catch(e) {
            console.error(e.message);
        }
    }

    const getFormattedLandingData = () => {

        // declare data for currently selected landing
        let data = landingProps[content.requestor] || {};

        // loop through component types and determine if request formatting is needed
        Object.keys(data).forEach(key => {

            // skip entry if no data was provided
            if(!data[key].value) {
                return;
            }

            // determine which steps to take based on component type
            switch(data[key].component) {
                case workflowLandingComponents.date_picker:
                data = update(data, {
                    [key]: {
                        value: {
                            $set: data[key].value.utc().unix()
                        }
                    }
                });
                break;

                case workflowLandingComponents.division_picker:
                case workflowLandingComponents.region_picker:
                data = update(data, {
                    [key]: {
                        value: {
                            $set: data[key].value.id
                        }
                    }
                });
                break;
            }
        });
        return data;
    }

    const getLoadingComponent = () => {
        return (
            <div 
            id={'lottie_loading_component'}
            style={{
                alignItems: 'center',
                display: 'flex',
                height: '100dvh',
                justifyContent: 'center',
                width: '100%'
            }}>
                <LottieView
                autoPlay={true}
                loop={true}
                source={theme === 'light' ? require('files/lottie/loading-dots-grey.json') : require('files/lottie/loading-dots-white.json')}
                style={{
                    height: 50,
                    width: 50
                }}/>
            </div>
        )
    }

    const getMaxContentWidth = () => {

        // use the width of the device is the device is a mobile device
        if(Utils.isMobile() === true) {
            return size.width;
        }

        // determine if the content represents a knowledgebase article
        if(article) {
            return browserRequest && browserRequest.frame !== true ? 1000 : size.width;
        }

        // determine if an embedded workflow was provided
        if(session && session.options.display.embedded === true && session.options.display.embedded_max_width) {
            return parseInt(session.options.display.embedded_max_width);
        }

        // fallback to using browser request iframe variable as the determining factor for size calculation
        return browserRequest && browserRequest.frame !== true ? 750 : size.width;
    }

    const getResponseInputComponents = () => {

        // return default components if custom components were not provided
        if(responseHasCustomFields() === false) {
            return (
                <>
                <TextField
                containerStyle={{ 
                    marginBottom: 8,
                    width: '100%' 
                }}
                expandWithText={true} 
                onChange={setResponseText}
                placeholder={'Start typing your message here...'}
                value={responseText} />

                <FilePickerField
                accepted={['doc','docx','image/jpeg','image/png','pdf','txt','xls','xlsx']}
                containerStyle={{ 
                    marginBottom: 24,
                    width: '100%' 
                }}
                onChange={setResponseAttachment} 
                placeholder={'Click to browse for a file...'} />
                </>
            )
        }

        // return edit field mapper with custom field components if applicable
        if(response) {
            return response.content.value.map((item, index, values) => {
                return (
                    <div 
                    key={index}
                    style={{
                        marginBottom: index !== values.length - 1 ? 8 : 24
                    }}>
                        {getComponentForItem(item, index)}
                    </div>
                );
            })
        }
    }

    const getComponentForItem = (item, index) => {

        // prepare required component
        let requiredComponent = item.required && !responseValue[item.id] && (
            <div style={{
                backgroundColor: Appearance.colors.red,
                borderRadius: 4,
                height: 8,
                marginRight: 8,
                minHeight: 8,
                minWidth: 8,
                overflow: 'hidden',
                width: 8
            }} />
        )

        // determine which component type is needed
        switch(item.type) {
            case workflowLandingComponents.address_lookup:
            return (
                <AddressLookupField
                insetLabel={item.title}
                key={index}
                onChange={result => {
                    setResponseValue(props => {
                        return update(props, {
                            [item.id]: {
                                $set: result
                            }
                        });
                    });
                }} 
                placeholder={'Search by name or address...'}
                prependComponent={requiredComponent}
                value={responseValue[item.id]} />
            )

            case workflowLandingComponents.date_picker:
            return (
                <DatePickerField
                insetLabel={item.title}
                key={index}
                onChange={result => {
                    setResponseValue(props => {
                        return update(props, {
                            [item.id]: {
                                $set: result
                            }
                        });
                    });
                }} 
                prependComponent={requiredComponent}
                value={responseValue[item.id]} />
            )

            case workflowLandingComponents.file_picker:
            return (
                <FilePickerField
                accepted={['doc','docx','image/jpeg','image/png','pdf','txt','xls','xlsx']}
                insetLabel={item.title}
                key={index}
                onChange={result => {
                    setResponseValue(props => {
                        return update(props, {
                            [item.id]: {
                                $set: result
                            }
                        });
                    });
                }} 
                prependComponent={requiredComponent}
                value={responseValue[item.id]} />
            )

            case workflowLandingComponents.phone_number_field:
            return (
                <TextField
                insetLabel={item.title}
                key={index}
                format={'phone_number'}
                onChange={result => {
                    setResponseValue(props => {
                        return update(props, {
                            [item.id]: {
                                $set: result
                            }
                        });
                    });
                }} 
                prependComponent={requiredComponent}
                value={responseValue[item.id]} />
            )

            case workflowLandingComponents.textview:
            return (
                <TextField
                expandWithText={true}
                insetLabel={item.title}
                key={index}
                onChange={result => {
                    setResponseValue(props => {
                        return update(props, {
                            [item.id]: {
                                $set: result
                            }
                        });
                    });
                }} 
                prependComponent={requiredComponent}
                value={responseValue[item.id]} />
            )

            default:
            return (
                <TextField
                insetLabel={item.title}
                key={index}
                onChange={result => {
                    setResponseValue(props => {
                        return update(props, {
                            [item.id]: {
                                $set: result
                            }
                        });
                    });
                }} 
                prependComponent={requiredComponent}
                value={responseValue[item.id]} />
            )
        }
    }

    const getWorkflowDisplayOptions = display => {
        if(display.embedded === true) {
            if(Utils.isMobile() === true) {
                return {
                    padding: 0,
                    width: '100vw'
                }
            }
            return {
                maxWidth: session.options.display.embedded_max_width ? parseInt(session.options.display.embedded_max_width) : null,
                minWidth: session.options.display.embedded_min_width ? parseInt(session.options.display.embedded_min_width) : null,
                width: 'calc(100vw - 40px)'
            }
        }
    }

    const getStructureContent = () => {
        return layers.map(({ Component, abstract, id, visible }, index) => {
            if(visible === false) {
                return null;
            }
            return (
                <Component
                abstract={abstract}
                key={index}
                index={index}
                utils={utils}
                options={{
                    index: index,
                    onClose: onCloseLayer,
                    zIndex: EndIndex - layerIndex.findIndex(prev => prev === id)
                }}/>
            )
        })
    }

    const prepareKBArticle = async id => {
        try {
            setLoading(true);
            let { article } = await Request.get(utils, '/support/', {
                id: id,
                type: 'kb_article_details'
            });
            
            setLoading(false);
            setArticle(article && {
                ...article,
                content: {
                    ...article.content,
                    layout: lz.decompress(lz.decodeBase64(article.content.layout))
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the details for this knowledge base article. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const responseHasCustomFields = () => {
        return response && response.type === 'additional_info' && Array.isArray(response.content.value) === true && response.content.value.length > 0;
    }

    const shouldDecodeLandingContent = string => {
        try {
            let data = atob(string);
            return JSON.parse(data) ? true : false;
        } catch(e) {
            return false;
        }
    }

    const startResponseSession = async id => {
        try {

            // fetch details for support request response
            setLoading(true);
            let { request, response } = await Request.get(utils, '/support/', {
                id: id,
                include_request: true,
                type: 'request_response_details'
            });

            setLoading(false);
            setRequest(request);
            setResponse(response);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing your response session. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const startSession = async () => {
        try {

            // declare request variables
            let request = QueryString.parse(window.location.search);
            setBrowserRequest(request);

            // offload request if a support request response feedback session was requested
            if(request.r) {
                startResponseSession(request.r);
                return;
            }

            // offload request if a kb article was requested
            if(request.kb) {
                prepareKBArticle(request.kb);
                return;
            }

            // prevent moving forward if no workflow identifier was found
            // do not throw an error, it's likely that someone tried to access this web app manually
            if(!request.id) {
                return;
            }

            // prepare workflow storage key
            let storageKey = `workflow.${request.id}`;

            // add dealership id to the storage key if applicable
            if(request.dealership_access_token) {

                // fetch dealership id from access token
                let { dealership } = await Request.get(utils, '/dealerships/', {
                    access_token: request.dealership_access_token,
                    type: 'get_dealership_from_access_token'
                });

                // update storage key with dealership id and update local ref with dealership id and name
                storageKey = `workflow.${request.id}.dealership.${dealership.id}`;
                dealershipRef.current = dealership;
            }

            // check if previous session token was stored for this workflow/dealership combination
            let string = localStorage.getItem(storageKey);
            let session = string ? JSON.parse(string) : {};

            // request new token if no local token was found
            let { is_new_token, options, token } = await Request.get(utils, '/support/', {
                channel: 1,
                dealership_id: dealershipRef.current && dealershipRef.current.id,
                id: request.id,
                mobile: request.mobile,
                token: session.token,
                type: 'start_session'
            });

            // store new interaction token if a new token was received
            if(is_new_token === true) {
                localStorage.setItem(storageKey, JSON.stringify({
                    date: moment().unix(),
                    token: token
                }));
            }

            // update session details state
            setSession({
                options: options,
                token: token
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing the session. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const utils = {
        alert: {
            dev: () => {
                utils.alert.show({
                    title: 'Under Development',
                    message: 'This feature is currently under development and will become available at a later date.'
                })
            },
            show: props => {
                setAlerts(alerts => {
                    return update(alerts, {
                        $push: [{
                            id: `${moment().unix()}-${Math.random()}`,
                            ...props
                        }]
                    });
                })
            }
        },
        api: {
            headers: () => ({
                'Content-Type': 'application/json',
                'X-Support-Gateway': `Token ${API.gateway_token}`,
                ...session && session.token && {
                    'X-Support-Token': `Token ${session.token}`
                },
                ...userRef.current && {
                    'Authorization': `Bearer ${userRef.current.api_key}`,
                    'Identification': `User ${userRef.current.user_id}`
                }
            })
        },
        content: Content,
        editor: {
            button: {
                onClick: onActionButtonClick
            },
            field: {
                get: key => landingProps[content.requestor] && landingProps[content.requestor][key] && landingProps[content.requestor][key].value,
                set: props => {
                    setLandingProps(prev => ({
                        ...prev,
                        [content.requestor]: {
                            ...prev[content.requestor],
                            ...props
                        } 
                    }));
                }
            }
        },
        events: {
            off: (key, callback) => {
                window.removeEventListener(key, callback);
            },
            on: (key, callback) => {
                window.addEventListener(key, callback);
            },
            emit: (key, props = {}) => {
                let evt = new CustomEvent(key, { detail: props });
                dispatchEvent(evt);
            }
        },
        layer: {
            close: onCloseLayer,
            open: onOpenLayer
        }
    }

    useEffect(() => {
        if(session) {
            onRequestPermissions();
        }
    }, [session]);

    useEffect(() => {
        onContentChange(content);
        onContentContainerSizeChange();
    }, [content]);

    useEffect(() => {
        if(seconds === 0 && shouldRedirect === true) {
            onPerformRedirection();
        }
    }, [seconds]);

    useEffect(() => {
        utils.events.emit('loading', loading);
    }, [loading]);

    useEffect(() => {

        // fetch support workflow details from server and either create a token or validate a cached token
        startSession();

        // size listener
        window.addEventListener('resize', onWindowSizeChange);

        // listen for history state changes
        window.onpopstate = onHistoryStateChange;

        // style change listeners
        document.body.style.overflowY = 'scroll';
        window.theme = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        document.documentElement.style.setProperty('--theme', window.theme);
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', onUpdateTheme);
        onSetStyleSheetProperties();

    }, []);

    return (
        <div
        className={`root-container ${theme}`}
        style={{
            alignItems: 'center',
            backgroundColor: getBackgroundColor(),
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: size.height,
            padding: 20,
            width: '100%',
            ...Utils.isMobile() && {
                padding: 0
            }
        }}>
            <div style={{
                height: Utils.isMobile() ? '100dvh' : 'auto',
                margin: 'auto',
                maxWidth: getMaxContentWidth(),
                overflowY: 'scroll'
            }}>
                {getContent()}
            </div>
            {getStructureContent()}
            <AlertStack>
                {alerts.map((alert, index) => (
                    <Alert
                    {...alert}
                    index={(alerts.length - 1) - index}
                    key={index}
                    onClose={onCloseAlert} 
                    utils={utils}/>
                ))}
            </AlertStack>
        </div>
    )
}

export default App;
